/**
 * 设备检测工具
 * 用于判断当前设备类型和能力
 */

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  supportsVirtualKeyboard: boolean;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
}

class DeviceDetector {
  private deviceInfo: DeviceInfo | null = null;

  /**
   * 获取设备信息
   */
  getDeviceInfo(): DeviceInfo {
    if (this.deviceInfo) {
      return this.deviceInfo;
    }

    if (typeof window === 'undefined') {
      // 服务端渲染时的默认值
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isTouchDevice: false,
        supportsVirtualKeyboard: false,
        userAgent: '',
        screenWidth: 1920,
        screenHeight: 1080
      };
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // 移动设备检测
    const mobileRegex = /android.*mobile|webos|iphone|ipod|blackberry|iemobile|opera mini/i;
    const tabletRegex = /ipad|android(?!.*mobile)|tablet/i;

    const isMobileUA = mobileRegex.test(userAgent);
    const isTabletUA = tabletRegex.test(userAgent);

    // 基于屏幕尺寸的判断（更宽松的桌面端判断）
    const isMobileScreen = screenWidth <= 480; // 更严格的移动端判断
    const isTabletScreen = screenWidth > 480 && screenWidth <= 1024;
    const isDesktopScreen = screenWidth > 1024;

    // 综合判断（优先考虑屏幕尺寸）
    const isMobile = isMobileUA || isMobileScreen;
    const isTablet = (isTabletUA || isTabletScreen) && !isMobile;
    const isDesktop = isDesktopScreen || (!isMobile && !isTablet);

    // 虚拟键盘支持判断
    // 桌面端支持 MathLive 虚拟键盘（包括触摸屏桌面设备），移动端不支持
    const supportsVirtualKeyboard = isDesktop;

    this.deviceInfo = {
      isMobile,
      isTablet,
      isDesktop,
      isTouchDevice,
      supportsVirtualKeyboard,
      userAgent,
      screenWidth,
      screenHeight
    };

    return this.deviceInfo;
  }

  /**
   * 是否为移动设备
   */
  isMobile(): boolean {
    return this.getDeviceInfo().isMobile;
  }

  /**
   * 是否为平板设备
   */
  isTablet(): boolean {
    return this.getDeviceInfo().isTablet;
  }

  /**
   * 是否为桌面设备
   */
  isDesktop(): boolean {
    return this.getDeviceInfo().isDesktop;
  }

  /**
   * 是否为触摸设备
   */
  isTouchDevice(): boolean {
    return this.getDeviceInfo().isTouchDevice;
  }

  /**
   * 是否支持虚拟键盘
   * 只有桌面端非触摸设备支持 MathLive 虚拟键盘
   */
  supportsVirtualKeyboard(): boolean {
    return this.getDeviceInfo().supportsVirtualKeyboard;
  }

  /**
   * 获取设备类型字符串
   */
  getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const info = this.getDeviceInfo();
    if (info.isMobile) return 'mobile';
    if (info.isTablet) return 'tablet';
    return 'desktop';
  }

  /**
   * 重置设备信息缓存
   * 用于窗口大小变化时重新检测
   */
  reset(): void {
    this.deviceInfo = null;
  }
}

// 单例实例
export const deviceDetector = new DeviceDetector();

// 便捷函数
export const isMobile = () => deviceDetector.isMobile();
export const isTablet = () => deviceDetector.isTablet();
export const isDesktop = () => deviceDetector.isDesktop();
export const isTouchDevice = () => deviceDetector.isTouchDevice();
export const supportsVirtualKeyboard = () => deviceDetector.supportsVirtualKeyboard();
export const getDeviceType = () => deviceDetector.getDeviceType();